import constate from 'constate';
import { t } from 'i18next';
import { has, times } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { ImportHeader, ImportType, SolveProblemsStep } from 'bom/enums';
import {
  useBomImportActions,
  useGetBomImportPreviewStatus,
  useGetBomImportStatus,
} from 'bom/hooks';
import {
  IImportColumnMapping,
  IImportCreate,
  IImportPartsData,
  IImportPartsStatus,
  IImportProblems,
} from 'bom/types';
import { getImportHeaderMappingKeys } from 'bom/utils';
import { FileExtension, FileUploadType, MimeType, NotificationPosition } from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { useNotificationContext } from 'core/states';

interface IProps {
  createImport: (requestPayload) => Promise<IImportCreate>;
  createImportPayload: Record<string, string | number>;
  type: ImportType;
  onSubmitSuccess?: (importData: IImportPartsStatus) => void;
  isAlternateVersionEnabled?: boolean;
  attachmentType?: FileUploadType;
}

const useBomPartsImport = ({
  createImport,
  createImportPayload,
  type,
  onSubmitSuccess,
  isAlternateVersionEnabled = false,
  attachmentType,
}: IProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<IImportCreate | null>(null);
  const [recognizedHeadersMapped, setRecognizedHeadersMapped] = useState<Record<
    number,
    string
  > | null>(null);
  const [partImportId, setPartImportId] = useState<number | null>(null);
  const [headerRowIndex, setHeaderRowIndex] = useState<number>(-1);
  const [mergeDuplicates, setMergeDuplicates] = useState<boolean>(true);
  const [updateMatchingLines, setUpdateMatchingLines] = useState<boolean>(true);
  const [previewCombineDuplicates, setPreviewCombineDuplicates] = useState<boolean | null>(null);
  const [ignoreEmptyQuantities, setIgnoreEmptyQuantities] = useState<boolean>(true);
  const [headerRow, setHeaderRow] = useState<string[] | null>(null);
  const [columnsMapping, setColumnsMapping] = useState<IImportColumnMapping[]>([]);
  const [previewStatusPromise, setPreviewStatusPromise] = useState<{
    resolve: (value: boolean) => void;
  } | null>(null);
  const [problems, setProblems] = useState<IImportProblems>({
    [SolveProblemsStep.UNKNOWN_MANUFACTURERS]: null,
    [SolveProblemsStep.ASSIGN_MANUFACTURERS]: null,
    [SolveProblemsStep.MULTIPLE_MANUFACTURERS]: null,
    [SolveProblemsStep.MEASURE_CONFLICT]: null,
  });
  const [importStatusPromise, setImportStatusPromise] = useState<{
    resolve: (value: boolean) => void;
  } | null>(null);
  const [completedImportId, setCompletedImportId] = useState<number | null>(null);

  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);

  const isQuoteImport = [ImportType.CUSTOMER_QUOTE, ImportType.VENDOR_QUOTE].includes(type);

  const queryClient = useQueryClient();

  const {
    showErrorNotification,
    showLoadingNotification,
    showSuccessNotification,
    clearNotification,
  } = useNotificationContext();

  const { fileUpload } = useFilesActions();
  const { createColumnAliases, createImportParts, importParts } = useBomImportActions();

  const onPreviewStatusSuccess = () => {
    clearNotification();

    if (previewStatusPromise) {
      previewStatusPromise.resolve(true);

      setPreviewStatusPromise(null);
      setIsLoadingNextStep(false);
    }
  };

  useGetBomImportPreviewStatus(
    { partImport: partImportId },
    Boolean(previewStatusPromise),
    onPreviewStatusSuccess,
    () => {
      previewStatusPromise.resolve(false);

      setPreviewStatusPromise(null);
      setIsLoadingNextStep(false);
    }
  );

  const onImportStatusSuccess = (data: IImportPartsStatus) => {
    if (importStatusPromise) {
      queryClient.invalidateQueries(`${type}Parts`);

      importStatusPromise.resolve(true);

      // Capture the finished import id so the post-import failed-rows modal can fetch its
      // skipped rows — the drawer auto-closes shortly after and clearData wipes partImportId.
      if (partImportId) {
        setCompletedImportId(partImportId);
      }

      onSubmitSuccess?.(data);
      setImportStatusPromise(null);
      setIsLoadingNextStep(false);
    }
  };

  const onImportStatusCheck = (data: IImportPartsStatus) => {
    if (data?.finished) {
      setIsLoadingNextStep(false);

      showSuccessNotification(
        {
          message: t('messageImportPartsStatusSuccess', {
            count: data?.imported || 0,
          }),
        },
        NotificationPosition.DRAWER
      );

      return;
    }

    showLoadingNotification(
      {
        message: t('messageImportPartsStatusInProgress', {
          imported: data?.imported || 0,
          total: data?.total,
        }),
      },
      NotificationPosition.DRAWER
    );
  };

  const onImportStatusError = () => {
    importStatusPromise.resolve(false);

    setImportStatusPromise(null);
    setIsLoadingNextStep(false);
  };

  useGetBomImportStatus(
    { partImport: partImportId },
    Boolean(importStatusPromise),
    onImportStatusSuccess,
    onImportStatusCheck,
    onImportStatusError
  );

  const acceptFileTypes = {
    [MimeType.XLS]: [FileExtension.XLS],
    [MimeType.XLSX]: [FileExtension.XLSX],
    [MimeType.CSV]: [FileExtension.CSV],
  };

  useEffect(() => {
    if (!importData) {
      return;
    }

    const numberOfColumns = importData?.colWidths?.length ?? 0;

    const columnsMapping = times(numberOfColumns, (index) => ({
      field: recognizedHeadersMapped?.[index] || headerRow?.[index] || `col${index}`,
      initialField: headerRow?.[index] || null,
      isMapped: !!recognizedHeadersMapped?.[index],
      colIndex: index,
    }));

    setColumnsMapping(columnsMapping);
  }, [headerRow, importData, importData?.colWidths?.length, recognizedHeadersMapped]);

  const mapRecognizedHeaders = useCallback(
    (importData: IImportCreate) => {
      if (!importData) {
        return;
      }

      const mappableHeaders = getImportHeaderMappingKeys(type);

      const recognizedHeadersMapped = Object.entries(importData?.recognizedHeaders)
        .filter(([key]) => mappableHeaders.includes(key as ImportHeader))
        .reduce((acc, [key, value]) => {
          acc[value.col] = key;

          return acc;
        }, {});

      setRecognizedHeadersMapped(recognizedHeadersMapped);
    },
    [type]
  );

  const setImportCreateData = useCallback(
    (importData: IImportCreate) => {
      mapRecognizedHeaders(importData);
      setImportData(importData);
      setPartImportId(importData?.importId ?? null);

      let fileHeaderRowIndex = -1;
      if (Object.keys(importData.recognizedHeaders).length) {
        fileHeaderRowIndex =
          importData.recognizedHeaders[Object.keys(importData.recognizedHeaders)[0]].row;
      }
      setHeaderRowIndex(fileHeaderRowIndex);
      setIsLoadingNextStep(false);
    },
    [mapRecognizedHeaders]
  );

  const onCreateImport = useCallback(
    async (fileId?: string) => {
      setIsLoadingNextStep(true);

      if (!fileId) {
        const { data } = await fileUpload(file, {
          fileType: FileUploadType.BOM_IMPORT,
        });

        fileId = data[0].id as string;
      }

      const importData = await createImport({
        ...createImportPayload,
        file: fileId,
      });

      setImportCreateData(importData);
    },
    [createImport, createImportPayload, file, fileUpload, setImportCreateData]
  );

  const onConfirmMapping = async (): Promise<boolean> => {
    const mappedColumns = columnsMapping.filter((column) => column.isMapped);

    const headerColumns = mappedColumns.reduce((acc, column) => {
      acc[column.field] = column.colIndex;

      return acc;
    }, {});

    if (!has(headerColumns, ImportHeader.NUMBER) && !has(headerColumns, ImportHeader.DESCRIPTION)) {
      showErrorNotification(
        { message: t('errorPreviewBomImportHeadersRequired') },
        NotificationPosition.DRAWER
      );

      return false;
    }

    const columnAliases = mappedColumns.reduce((acc, column) => {
      acc[column.field] = column.initialField;

      return acc;
    }, {});

    setIsLoadingNextStep(true);

    const { success: successColumnAliases } = await createColumnAliases({
      aliases: columnAliases,
    });

    if (!successColumnAliases) {
      setIsLoadingNextStep(false);

      return false;
    }

    const { success: successPreview } = await createImportParts({
      partImport: partImportId,
      headerRow: 0,
      headerCols: headerColumns,
      options: {
        headerRowIndex,
        // Old backends only read merge_duplicates; new ones prefer combine_duplicate_rows when
        // present — both must carry the same value.
        mergeDuplicates: +mergeDuplicates,
        ignoreEmptyQuantities: +ignoreEmptyQuantities,
        ...(isQuoteImport && { combineDuplicateRows: +mergeDuplicates }),
      },
    });

    if (!successPreview) {
      setIsLoadingNextStep(false);

      return false;
    }

    setPreviewCombineDuplicates(mergeDuplicates);

    return new Promise<boolean>((resolve) => {
      setPreviewStatusPromise({ resolve });
    });
  };

  // Updating existing quote lines is only valid when the current preview was prepared with
  // combined duplicate rows — never send update_matching_lines=1 for an all-rows preview.
  const shouldUpdateMatchingLines =
    isQuoteImport && updateMatchingLines && true === previewCombineDuplicates;

  const onImportParts = async (): Promise<boolean> => {
    setIsLoadingNextStep(true);

    const { success } = await importParts({
      partImport: partImportId,
      // Old backends only read merge_duplicates (absent = add all); new ones prefer
      // update_matching_lines when present — both must carry the same value.
      mergeDuplicates: isQuoteImport ? +shouldUpdateMatchingLines : +mergeDuplicates,
      ...(isQuoteImport && { updateMatchingLines: +shouldUpdateMatchingLines }),
      applyCost: 0,
      applyPrice: 0,
      ...(attachmentType && { attachmentType }),
    } as IImportPartsData);

    if (!success) {
      setIsLoadingNextStep(false);

      return;
    }

    return new Promise<boolean>((resolve) => {
      setImportStatusPromise({ resolve });
    });
  };

  const clearData = () => {
    clearNotification();
    setFile(null);
    setPartImportId(null);
  };

  return {
    acceptFileTypes,
    file,
    setFile,
    onCreateImport,
    importData,
    setImportCreateData,
    clearRecognizedHeaders: () => setRecognizedHeadersMapped(null),
    partImportId,
    numberOfColumns: columnsMapping?.length,
    headerRowIndex,
    setHeaderRowIndex,
    mergeDuplicates,
    setMergeDuplicates,
    updateMatchingLines,
    setUpdateMatchingLines,
    previewCombineDuplicates,
    shouldUpdateMatchingLines,
    isQuoteImport,
    ignoreEmptyQuantities,
    setIgnoreEmptyQuantities,
    headerRow,
    setHeaderRow,
    columnsMapping,
    onConfirmMapping,
    setColumnsMapping,
    problems,
    setProblems,
    onImportParts,
    clearData,
    isLoadingNextStep,
    type,
    isAlternateVersionEnabled,
    completedImportId,
    clearCompletedImport: () => setCompletedImportId(null),
  };
};

export const [BomPartsImportProvider, useBomPartsImportContext] = constate(useBomPartsImport);
